import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { buildProductCreateBody } from './product-body';
import { updateProduct } from './product-update';
import {
  type BulkEditFormValues,
  type OptionFormValues,
  type OptionValueFormValues,
  type ProductFormValues,
  type VariantFormValues,
} from './schemas';
import type {
  Category,
  Product,
  ProductImage,
  ProductOptionValue,
  ProductOptionWithValues,
  ProductVariant,
  ProductWithStock,
} from './types';

import { apiRequest, type ApiError } from '@/lib/api-client';
import { env } from '@/lib/env';
import type { ImageSizeVerdict } from '@/lib/image-size';
import { useSession } from '@/lib/session';

/**
 * Every hook here calls exactly one real endpoint — see the table in the
 * plan message (docs/resumée.txt, this lot's entry) for the full mapping.
 * No hook here fetches anything the API does not actually expose.
 */

function variantToBody(values: VariantFormValues): Record<string, unknown> {
  const attributes: Record<string, string> = {};
  for (const pair of values.attributes) {
    attributes[pair.key] = pair.value;
  }
  return { sku: values.sku, attributes, priceOverride: values.priceOverride ?? null };
}

export function useCategoriesQuery(): UseQueryResult<readonly Category[], ApiError> {
  const { accessToken, activeShopId } = useSession();
  return useQuery({
    queryKey: ['categories', activeShopId],
    queryFn: () =>
      apiRequest<readonly Category[]>('/merchant/categories', {
        accessToken,
        shopId: activeShopId,
      }),
    enabled: accessToken !== null && activeShopId !== null,
  });
}

export function useProductsQuery(): UseQueryResult<readonly ProductWithStock[], ApiError> {
  const { accessToken, activeShopId } = useSession();
  return useQuery({
    queryKey: ['products', activeShopId],
    queryFn: () =>
      apiRequest<readonly ProductWithStock[]>('/merchant/products', {
        accessToken,
        shopId: activeShopId,
      }),
    enabled: accessToken !== null && activeShopId !== null,
  });
}

export function useProductQuery(
  productId: string | undefined,
): UseQueryResult<ProductWithStock, ApiError> {
  const { accessToken, activeShopId } = useSession();
  return useQuery({
    queryKey: ['products', activeShopId, productId],
    queryFn: () =>
      apiRequest<ProductWithStock>(`/merchant/products/${productId ?? ''}`, {
        accessToken,
        shopId: activeShopId,
      }),
    enabled: accessToken !== null && activeShopId !== null && productId !== undefined,
  });
}

export function useCreateProductMutation(): UseMutationResult<Product, Error, ProductFormValues> {
  const { accessToken, activeShopId } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: ProductFormValues) =>
      apiRequest<Product>('/merchant/products', {
        method: 'POST',
        body: buildProductCreateBody(values),
        accessToken,
        shopId: activeShopId,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['products', activeShopId] });
    },
  });
}

/**
 * Lot 1 / 1C - marks or unmarks ONE product as a "coup de coeur" through its
 * own narrow endpoint. It is deliberately NOT `useUpdateProductMutation`: that
 * is a full-replace PUT of the whole form, and a one-click star must never
 * resend (or blank) the fields it does not own.
 */
export function useSetProductFeaturedMutation(): UseMutationResult<
  Product,
  Error,
  { readonly productId: string; readonly isFeatured: boolean }
> {
  const { accessToken, activeShopId } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, isFeatured }: { productId: string; isFeatured: boolean }) =>
      apiRequest<Product>(`/merchant/products/${productId}/featured`, {
        method: 'PUT',
        body: { isFeatured },
        accessToken,
        shopId: activeShopId,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['products', activeShopId] });
    },
  });
}

export function useUpdateProductMutation(
  productId: string,
): UseMutationResult<Product, Error, ProductFormValues> {
  const { accessToken, activeShopId } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    // Read-modify-write (see product-update.ts): the fields this form does
    // not render are sent back exactly as stored, never blanked.
    mutationFn: (values: ProductFormValues) =>
      updateProduct(productId, values, { accessToken, shopId: activeShopId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['products', activeShopId] });
    },
  });
}

export function useDeleteProductMutation(): UseMutationResult<null, Error, string> {
  const { accessToken, activeShopId } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (productId: string) =>
      apiRequest<null>(`/merchant/products/${productId}`, {
        method: 'DELETE',
        accessToken,
        shopId: activeShopId,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['products', activeShopId] });
    },
  });
}

export function useVariantsQuery(
  productId: string | undefined,
): UseQueryResult<readonly ProductVariant[], ApiError> {
  const { accessToken, activeShopId } = useSession();
  return useQuery({
    queryKey: ['products', activeShopId, productId, 'variants'],
    queryFn: () =>
      apiRequest<readonly ProductVariant[]>(`/merchant/products/${productId ?? ''}/variants`, {
        accessToken,
        shopId: activeShopId,
      }),
    enabled: accessToken !== null && activeShopId !== null && productId !== undefined,
  });
}

export function useCreateVariantMutation(
  productId: string,
): UseMutationResult<ProductVariant, Error, VariantFormValues> {
  const { accessToken, activeShopId } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: VariantFormValues) =>
      apiRequest<ProductVariant>(`/merchant/products/${productId}/variants`, {
        method: 'POST',
        body: variantToBody(values),
        accessToken,
        shopId: activeShopId,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['products', activeShopId, productId, 'variants'],
      });
    },
  });
}

export function useDeleteVariantMutation(
  productId: string,
): UseMutationResult<null, Error, string> {
  const { accessToken, activeShopId } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variantId: string) =>
      apiRequest<null>(`/merchant/products/${productId}/variants/${variantId}`, {
        method: 'DELETE',
        accessToken,
        shopId: activeShopId,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['products', activeShopId, productId, 'variants'],
      });
    },
  });
}

// ---------------------------------------------------------------------
// Variant-improvements lot — structured options, the generator, variant
// status toggle, and bulk edit. Same nesting/query-key convention as
// the variants hooks above (`['products', activeShopId, productId,
// 'options'|'variants']`); a write to options also invalidates the
// variants list (renaming/generating rewrites `attributes`) and the
// product itself (`variantOptionsReviewedAt`).
// ---------------------------------------------------------------------

function optionToBody(values: OptionFormValues): Record<string, unknown> {
  return { name: values.name, displayType: values.displayType };
}

function optionValueToBody(values: OptionValueFormValues): Record<string, unknown> {
  return {
    value: values.value,
    // Omitted entirely when untouched — see optionValueFormSchema's own
    // header for why this must not become a literal `swatchHex: undefined`
    // key (JSON.stringify drops that either way, but the intent is
    // explicit here rather than incidental).
    ...(values.swatchHex !== undefined ? { swatchHex: values.swatchHex } : {}),
  };
}

function invalidateOptionsAndVariants(
  queryClient: ReturnType<typeof useQueryClient>,
  activeShopId: string | null,
  productId: string,
): void {
  void queryClient.invalidateQueries({
    queryKey: ['products', activeShopId, productId, 'options'],
  });
  void queryClient.invalidateQueries({
    queryKey: ['products', activeShopId, productId, 'variants'],
  });
  void queryClient.invalidateQueries({ queryKey: ['products', activeShopId, productId] });
}

export function useOptionsQuery(
  productId: string | undefined,
): UseQueryResult<readonly ProductOptionWithValues[], ApiError> {
  const { accessToken, activeShopId } = useSession();
  return useQuery({
    queryKey: ['products', activeShopId, productId, 'options'],
    queryFn: () =>
      apiRequest<readonly ProductOptionWithValues[]>(
        `/merchant/products/${productId ?? ''}/options`,
        { accessToken, shopId: activeShopId },
      ),
    enabled: accessToken !== null && activeShopId !== null && productId !== undefined,
  });
}

export function useCreateOptionMutation(
  productId: string,
): UseMutationResult<ProductOptionWithValues, Error, OptionFormValues> {
  const { accessToken, activeShopId } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: OptionFormValues) =>
      apiRequest<ProductOptionWithValues>(`/merchant/products/${productId}/options`, {
        method: 'POST',
        body: optionToBody(values),
        accessToken,
        shopId: activeShopId,
      }),
    onSuccess: () => {
      invalidateOptionsAndVariants(queryClient, activeShopId, productId);
    },
  });
}

export function useRenameOptionMutation(
  productId: string,
): UseMutationResult<
  ProductOptionWithValues,
  Error,
  { optionId: string; values: OptionFormValues }
> {
  const { accessToken, activeShopId } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ optionId, values }) =>
      apiRequest<ProductOptionWithValues>(`/merchant/products/${productId}/options/${optionId}`, {
        method: 'PATCH',
        body: optionToBody(values),
        accessToken,
        shopId: activeShopId,
      }),
    onSuccess: () => {
      invalidateOptionsAndVariants(queryClient, activeShopId, productId);
    },
  });
}

export function useDeleteOptionMutation(productId: string): UseMutationResult<null, Error, string> {
  const { accessToken, activeShopId } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (optionId: string) =>
      apiRequest<null>(`/merchant/products/${productId}/options/${optionId}`, {
        method: 'DELETE',
        accessToken,
        shopId: activeShopId,
      }),
    onSuccess: () => {
      invalidateOptionsAndVariants(queryClient, activeShopId, productId);
    },
  });
}

export function useCreateOptionValueMutation(
  productId: string,
): UseMutationResult<
  ProductOptionValue,
  Error,
  { optionId: string; values: OptionValueFormValues }
> {
  const { accessToken, activeShopId } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ optionId, values }) =>
      apiRequest<ProductOptionValue>(`/merchant/products/${productId}/options/${optionId}/values`, {
        method: 'POST',
        body: optionValueToBody(values),
        accessToken,
        shopId: activeShopId,
      }),
    onSuccess: () => {
      invalidateOptionsAndVariants(queryClient, activeShopId, productId);
    },
  });
}

export function useUpdateOptionValueMutation(
  productId: string,
): UseMutationResult<
  ProductOptionValue,
  Error,
  { optionId: string; valueId: string; values: OptionValueFormValues }
> {
  const { accessToken, activeShopId } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ optionId, valueId, values }) =>
      apiRequest<ProductOptionValue>(
        `/merchant/products/${productId}/options/${optionId}/values/${valueId}`,
        { method: 'PATCH', body: optionValueToBody(values), accessToken, shopId: activeShopId },
      ),
    onSuccess: () => {
      invalidateOptionsAndVariants(queryClient, activeShopId, productId);
    },
  });
}

export function useDeleteOptionValueMutation(
  productId: string,
): UseMutationResult<null, Error, { optionId: string; valueId: string }> {
  const { accessToken, activeShopId } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ optionId, valueId }) =>
      apiRequest<null>(`/merchant/products/${productId}/options/${optionId}/values/${valueId}`, {
        method: 'DELETE',
        accessToken,
        shopId: activeShopId,
      }),
    onSuccess: () => {
      invalidateOptionsAndVariants(queryClient, activeShopId, productId);
    },
  });
}

export function useMarkOptionsReviewedMutation(
  productId: string,
): UseMutationResult<null, Error, void> {
  const { accessToken, activeShopId } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiRequest<null>(`/merchant/products/${productId}/options/reviewed`, {
        method: 'POST',
        accessToken,
        shopId: activeShopId,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['products', activeShopId, productId] });
    },
  });
}

export interface GenerateCombinationInput {
  readonly optionValueIds: readonly string[];
  readonly sku: string;
  readonly priceOverride?: number | null;
}

export function useGenerateVariantsMutation(
  productId: string,
): UseMutationResult<readonly ProductVariant[], Error, readonly GenerateCombinationInput[]> {
  const { accessToken, activeShopId } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (combinations: readonly GenerateCombinationInput[]) =>
      apiRequest<readonly ProductVariant[]>(`/merchant/products/${productId}/variants/generate`, {
        method: 'POST',
        body: { combinations },
        accessToken,
        shopId: activeShopId,
      }),
    onSuccess: () => {
      invalidateOptionsAndVariants(queryClient, activeShopId, productId);
    },
  });
}

export function useSetVariantStatusMutation(
  productId: string,
): UseMutationResult<ProductVariant, Error, { variantId: string; isActive: boolean }> {
  const { accessToken, activeShopId } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ variantId, isActive }) =>
      apiRequest<ProductVariant>(`/merchant/products/${productId}/variants/${variantId}/status`, {
        method: 'PATCH',
        body: { isActive },
        accessToken,
        shopId: activeShopId,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['products', activeShopId, productId, 'variants'],
      });
    },
  });
}

export type BulkUpdateSelector =
  { readonly variantIds: readonly string[] } | { readonly optionValueIds: readonly string[] };

export interface BulkUpdateVariantsBody {
  readonly selector: BulkUpdateSelector;
  readonly priceAdjustment?: { readonly mode: 'relative' | 'absolute'; readonly amount: number };
  readonly stockDelta?: number;
  readonly quantityMinThreshold?: number | null;
  readonly isActive?: boolean;
  readonly reason?: string | null;
}

export interface BulkUpdateResult {
  readonly updated: number;
  readonly variants: readonly ProductVariant[];
}

export function bulkEditFormToBody(
  selector: BulkUpdateSelector,
  values: BulkEditFormValues,
): BulkUpdateVariantsBody {
  return {
    selector,
    ...(values.priceMode !== 'none' && values.priceAmount !== undefined
      ? { priceAdjustment: { mode: values.priceMode, amount: values.priceAmount } }
      : {}),
    ...(values.stockDelta !== undefined && values.stockDelta !== 0
      ? { stockDelta: values.stockDelta }
      : {}),
    ...(values.quantityMinThreshold !== undefined
      ? { quantityMinThreshold: values.quantityMinThreshold }
      : {}),
    ...(values.isActive !== 'unchanged' ? { isActive: values.isActive === 'activate' } : {}),
    ...(values.reason !== undefined && values.reason !== '' ? { reason: values.reason } : {}),
  };
}

export function useBulkUpdateVariantsMutation(
  productId: string,
): UseMutationResult<BulkUpdateResult, Error, BulkUpdateVariantsBody> {
  const { accessToken, activeShopId } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: BulkUpdateVariantsBody) =>
      apiRequest<BulkUpdateResult>(`/merchant/products/${productId}/variants/bulk-update`, {
        method: 'POST',
        body,
        accessToken,
        shopId: activeShopId,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['products', activeShopId, productId, 'variants'],
      });
    },
  });
}

export interface UploadImageInput {
  readonly file: File;
  readonly altText?: string;
}

/** The stored image plus the API's verdict on its size (see `lib/image-size.ts`). */
export type UploadedProductImage = ProductImage & { readonly size?: ImageSizeVerdict };

/**
 * `POST /merchant/products/:id/images` — multipart, field name "file"
 * (product-image.controller.ts's `FileInterceptor('file')`).
 *
 * Images Produits lot — invalidates the real image list (below) on success,
 * so the gallery reflects this upload without a manual refresh. No delete,
 * no reorder endpoint exists yet — this mutation remains the only WRITE this
 * file exposes for images.
 */
export function useUploadProductImageMutation(
  productId: string,
): UseMutationResult<UploadedProductImage, Error, UploadImageInput> {
  const { accessToken, activeShopId } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, altText }: UploadImageInput) => {
      const form = new FormData();
      form.append('file', file);
      if (altText !== undefined && altText.trim() !== '') {
        form.append('altText', altText.trim());
      }
      return apiRequest<UploadedProductImage>(`/merchant/products/${productId}/images`, {
        method: 'POST',
        body: form,
        accessToken,
        shopId: activeShopId,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['products', activeShopId, productId, 'images'],
      });
    },
  });
}

/**
 * Images Produits lot — `GET /merchant/products/:id/images`, the merchant's
 * own real, persisted gallery (any product status, draft included). Closes
 * the gap `ImageUploader.tsx` used to document: before this lot, nothing
 * anywhere could re-fetch what was already uploaded.
 */
export function useProductImagesQuery(
  productId: string | undefined,
): UseQueryResult<readonly ProductImage[], ApiError> {
  const { accessToken, activeShopId } = useSession();
  return useQuery({
    queryKey: ['products', activeShopId, productId, 'images'],
    queryFn: () =>
      apiRequest<readonly ProductImage[]>(`/merchant/products/${productId ?? ''}/images`, {
        accessToken,
        shopId: activeShopId,
      }),
    enabled: accessToken !== null && activeShopId !== null && productId !== undefined,
  });
}

/**
 * Images Produits lot — resolves one image to a displayable, same-origin
 * blob URL. `<img src>` cannot carry an `Authorization` header, and this
 * route (`GET .../images/:imageId/raw`) requires one (`catalog:write`) —
 * so this fetches the bytes itself (the API 302s to a real, momentarily
 * signed S3 URL; `fetch` follows that redirect transparently) and hands
 * back a local object URL. The caller owns that URL's lifecycle
 * (`URL.revokeObjectURL` on unmount/replacement) — the same discipline
 * `ImageUploader.tsx` already established for its local file previews.
 */
export async function fetchProductImageObjectUrl(
  productId: string,
  imageId: string,
  accessToken: string,
  shopId: string,
): Promise<string> {
  const response = await fetch(
    `${env.apiBaseUrl}/api/v1/merchant/products/${productId}/images/${imageId}/raw`,
    { headers: { Authorization: `Bearer ${accessToken}`, 'X-Shop-Id': shopId } },
  );
  if (!response.ok) {
    throw new Error(`Failed to load image ${imageId} (status ${String(response.status)}).`);
  }
  const blob = await response.blob();
  return URL.createObjectURL(blob);
}
